import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from './utils/api';
import { useAuth } from './hooks/useAuth';
import './GoldDatasetManager.css';

const LABELS = ['COSMETIC', 'OTC_DRUG', 'DRUG'];
const STATUSES = ['pending', 'in_review', 'needs_discussion', 'signed', 'rejected'];
const STATUS_COLOR = {
  pending: '#6b7280', in_review: '#2563eb', needs_discussion: '#d97706',
  signed: '#16a34a', rejected: '#dc2626',
};
// Verdict colours: safer→greener, more-regulated→redder (matches the report scoring hue).
const LABEL_COLOR = { COSMETIC: '#16a34a', OTC_DRUG: '#d97706', DRUG: '#dc2626' };

// Sort presets. Default is "least confident first" so testers triage the shakiest
// AI verdicts before the easy ones.
const SORTS = [
  { key: 'ai_asc', label: 'AI confidence — least first', sort: 'ai_confidence', order: 'asc' },
  { key: 'ai_desc', label: 'AI confidence — most first', sort: 'ai_confidence', order: 'desc' },
  { key: 'vp_asc', label: 'VeriPass confidence — least first', sort: 'veripass_confidence', order: 'asc' },
  { key: 'recent', label: 'Recently updated', sort: '', order: '' },
];

const pct = (c) => (c === null || c === undefined ? '—' : `${Math.round(c * 100)}%`);
const confColor = (c) =>
  c === null || c === undefined ? '#6b7280' : c < 0.6 ? '#dc2626' : c < 0.8 ? '#d97706' : '#16a34a';

function Badge({ text, color }) {
  return <span className="gdm-badge" style={{ background: `${color}22`, color, borderColor: `${color}55` }}>{text}</span>;
}
function Verdict({ label }) {
  if (!label) return <span className="gdm-dim">—</span>;
  return <Badge text={label} color={LABEL_COLOR[label] || '#6b7280'} />;
}
function Conf({ value }) {
  return <span className="gdm-conf" style={{ color: confColor(value) }}>{pct(value)}</span>;
}

export default function GoldDatasetManager() {
  const { user } = useAuth();
  const isSiteAdmin = !!(user?.is_root || user?.can_manage_admins);

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [coverage, setCoverage] = useState({ ai: 0, vp: 0 });
  const [filters, setFilters] = useState({ status: '', label: '', q: '' });
  const [sortKey, setSortKey] = useState('ai_asc');   // default: least-confident-first
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);       // detail {candidate, signoffs, revisions}
  const [draft, setDraft] = useState(null);    // editable copy
  const [signLabel, setSignLabel] = useState('');
  const [comment, setComment] = useState('');
  const [err, setErr] = useState('');
  const [aiBusy, setAiBusy] = useState(false);       // per-candidate re-run
  const [bulk, setBulk] = useState(null);            // { processed, remaining } while running
  const stopBulk = useRef(false);

  const fetchStats = useCallback(async () => {
    try { const r = await apiFetch('/api/v1/admin/gold/stats'); if (r.ok) setStats(await r.json()); } catch {}
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const s = SORTS.find((x) => x.key === sortKey) || SORTS[0];
      const p = new URLSearchParams({ page: String(page), page_size: '50' });
      if (filters.status) p.set('status', filters.status);
      if (filters.label) p.set('label', filters.label);
      if (filters.q) p.set('q', filters.q);
      if (s.sort) { p.set('sort', s.sort); p.set('order', s.order); }
      const r = await apiFetch(`/api/v1/admin/gold/candidates?${p}`);
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []); setTotal(d.total || 0);
        setCoverage({ ai: d.ai_classified || 0, vp: d.veripass_classified || 0 });
      }
    } finally { setLoading(false); }
  }, [filters, page, sortKey]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (id) => {
    setErr('');
    const r = await apiFetch(`/api/v1/admin/gold/candidates/${id}`);
    if (r.ok) {
      const d = await r.json();
      setSel(d); setDraft({ ...d.candidate });
      setSignLabel(d.candidate.expected_label || d.candidate.ai_verdict || d.candidate.auto_label || '');
      setComment('');
    }
  };
  const closeDetail = () => { setSel(null); setDraft(null); setErr(''); };

  const refresh = async (id) => { await fetchStats(); await fetchList(); if (id) await openDetail(id); };

  const saveEdit = async () => {
    setErr('');
    const c = sel.candidate;
    const fields = {
      gold_rationale: draft.gold_rationale, controlling_citation: draft.controlling_citation,
      regulatory_citations: draft.regulatory_citations, source_type: draft.source_type,
      source_url: draft.source_url, source_document_id: draft.source_document_id,
      provenance_tier: draft.provenance_tier, ingredient_entities: draft.ingredient_entities,
      boundary_tags: draft.boundary_tags, review_flags: draft.review_flags,
      notes: draft.notes, requires_dual_signoff: draft.requires_dual_signoff,
    };
    const r = await apiFetch(`/api/v1/admin/gold/candidates/${c.id}`, {
      method: 'PATCH', body: JSON.stringify({ version: c.version, fields }),
    });
    if (r.ok) { await refresh(c.id); }
    else { const e = await r.json().catch(() => ({})); setErr(e.detail === 'stale_version' ? 'Someone else edited this — reload.' : (e.detail || 'Save failed')); }
  };

  const act = async (verb, bodyObj) => {
    setErr('');
    const c = sel.candidate;
    const r = await apiFetch(`/api/v1/admin/gold/candidates/${c.id}/${verb}`, {
      method: 'POST', body: JSON.stringify(bodyObj || {}),
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { await refresh(c.id); }
    else { setErr(d.detail || `${verb} failed`); }
  };

  // Re-run the AI verdict + VeriPass report for the open candidate.
  const rerunAi = async () => {
    setErr(''); setAiBusy(true);
    try {
      const c = sel.candidate;
      const r = await apiFetch(`/api/v1/admin/gold/candidates/${c.id}/ai-classify`, {
        method: 'POST', body: JSON.stringify({ llm: true, veripass: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.candidate) { setSel((s) => ({ ...s, candidate: d.candidate })); await fetchList(); }
      else setErr(d.detail || 'AI classify failed');
    } finally { setAiBusy(false); }
  };

  // Loop the bulk endpoint until every row has a verdict (or the user stops it).
  const runAiMissing = async () => {
    if (!window.confirm('Run the AI (gpt-5.6) + VeriPass over every candidate still missing a verdict? '
      + 'This calls the LLM per claim and may take several minutes and incur cost.')) return;
    stopBulk.current = false; setErr(''); setBulk({ processed: 0, remaining: null });
    let processedTotal = 0;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (stopBulk.current) break;
        const r = await apiFetch('/api/v1/admin/gold/ai-classify', {
          method: 'POST', body: JSON.stringify({ limit: 5, only_missing: true, llm: true, veripass: true }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setErr(d.detail || 'AI batch failed'); break; }
        processedTotal += d.processed || 0;
        setBulk({ processed: processedTotal, remaining: d.remaining });
        await fetchList();
        if (!d.processed || !d.remaining) break;
      }
    } finally { setBulk(null); await refresh(); }
  };

  const createRelease = async () => {
    const tag = window.prompt('Immutable release tag (e.g. gold_v2):');
    if (!tag) return;
    const r = await apiFetch('/api/v1/admin/gold/releases', { method: 'POST', body: JSON.stringify({ version_tag: tag }) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) window.alert(`Released ${d.version}: ${d.count} items → ${d.export_path}`);
    else window.alert(d.detail || 'Release failed');
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));
  const csv = (v) => Array.isArray(v) ? v.join(', ') : (v || '');
  const parseList = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);
  const cand = sel?.candidate;
  const vp = cand?.veripass_report;

  return (
    <div className="gdm-wrap">
      <div className="gdm-head">
        <h1>Gold Dataset Manager</h1>
        <p className="gdm-sub">Adjudicate classification gold for AI/ML training · admin / root only</p>
      </div>

      {stats && (
        <div className="gdm-stats">
          <div className="gdm-stat"><b>{stats.totals?.signed || 0}</b><span>signed</span></div>
          <div className="gdm-stat"><b>{stats.totals?.pending || 0}</b><span>pending</span></div>
          <div className="gdm-stat"><b>{stats.signed_pct}%</b><span>complete</span></div>
          <div className="gdm-stat"><b>{coverage.ai}/{total}</b><span>AI scored</span></div>
          <div className="gdm-stat"><b>{coverage.vp}/{total}</b><span>VeriPass scored</span></div>
          {isSiteAdmin && !bulk && (
            <button className="gdm-btn gdm-btn-accent" onClick={runAiMissing}>Run AI on missing →</button>
          )}
          {bulk && (
            <div className="gdm-bulk">
              <span>AI scoring… {bulk.processed} done{bulk.remaining != null ? `, ${bulk.remaining} left` : ''}</span>
              <button className="gdm-btn gdm-btn-danger" onClick={() => { stopBulk.current = true; }}>Stop</button>
            </div>
          )}
          {isSiteAdmin && <button className="gdm-btn" onClick={createRelease}>Freeze release →</button>}
        </div>
      )}

      <div className="gdm-filters">
        <select value={filters.status} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.label} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, label: e.target.value })); }}>
          <option value="">All auto classes</option>
          {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={sortKey} onChange={(e) => { setPage(1); setSortKey(e.target.value); }} title="Sort worklist">
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <input placeholder="Search claim text…" value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchList(); } }} />
        <span className="gdm-count">{total} candidates</span>
      </div>

      <table className="gdm-table">
        <thead><tr>
          <th>Claim</th><th>AI verdict</th><th>AI conf</th><th>FDA regulation</th>
          <th>VeriPass</th><th>Verified</th><th>Status</th>
        </tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={7} className="gdm-empty">Loading…</td></tr>
            : items.length === 0 ? <tr><td colSpan={7} className="gdm-empty">No candidates</td></tr>
            : items.map((it) => {
              const disagree = it.ai_verdict && it.veripass_verdict && it.ai_verdict !== it.veripass_verdict;
              return (
                <tr key={it.id} onClick={() => openDetail(it.id)} className="gdm-row">
                  <td className="gdm-claim" title={it.claim_text}>{it.claim_text}</td>
                  <td><Verdict label={it.ai_verdict} /></td>
                  <td><Conf value={it.ai_confidence} /></td>
                  <td className="gdm-fda" title={it.fda_regulation || ''}>{it.fda_regulation || <span className="gdm-dim">—</span>}</td>
                  <td className="gdm-vpcell">
                    <Verdict label={it.veripass_verdict} />
                    {it.veripass_verdict && <span className="gdm-conf-sm" style={{ color: confColor(it.veripass_confidence) }}> {pct(it.veripass_confidence)}</span>}
                    {disagree && <span className="gdm-neq" title="AI and VeriPass disagree">≠</span>}
                  </td>
                  <td>{it.expected_label ? <Verdict label={it.expected_label} /> : <span className="gdm-dim">—</span>}</td>
                  <td><Badge text={it.status} color={STATUS_COLOR[it.status] || '#6b7280'} /></td>
                </tr>
              );
            })}
        </tbody>
      </table>

      <div className="gdm-pager">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
        <span>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
      </div>

      {sel && draft && (
        <div className="gdm-drawer-overlay" onClick={closeDetail}>
          <div className="gdm-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="gdm-drawer-head">
              <Badge text={cand.status} color={STATUS_COLOR[cand.status] || '#6b7280'} />
              {cand.requires_dual_signoff && <Badge text="dual sign-off" color="#d97706" />}
              <button className="gdm-x" onClick={closeDetail}>✕</button>
            </div>
            <h3 className="gdm-claimfull">{cand.claim_text}</h3>
            {cand.context_window && <p className="gdm-ctx">{cand.context_window}</p>}
            <div className="gdm-meta">Auto: <b>{cand.auto_label || '—'}</b> · tier {cand.provenance_tier} · src {cand.source_type}</div>
            {err && <div className="gdm-err">{err}</div>}

            {/* ── AI adjudication (pre-determined) ── */}
            <div className="gdm-aibox">
              <div className="gdm-aibox-head">
                <h4>AI verdict {cand.ai_model && <span className="gdm-dim">· {cand.ai_model}</span>}</h4>
                <button className="gdm-btn gdm-btn-sm" onClick={rerunAi} disabled={aiBusy}>
                  {aiBusy ? 'Running…' : 'Re-run AI + VeriPass'}
                </button>
              </div>
              {cand.ai_verdict ? (
                <>
                  <div className="gdm-airow">
                    <Verdict label={cand.ai_verdict} />
                    <span className="gdm-conf" style={{ color: confColor(cand.ai_confidence) }}>
                      confidence {pct(cand.ai_confidence)}
                    </span>
                  </div>
                  {cand.ai_citation && <div className="gdm-airow gdm-dim2">FDA: {cand.ai_citation}</div>}
                  {cand.ai_rationale && <p className="gdm-airat">{cand.ai_rationale}</p>}
                </>
              ) : <div className="gdm-dim">Not yet scored — use “Re-run AI + VeriPass”.</div>}
            </div>

            {/* ── VeriPass engine report ── */}
            <div className="gdm-aibox">
              <h4>VeriPass report</h4>
              {vp ? (
                <>
                  <div className="gdm-airow">
                    <Verdict label={vp.classification} />
                    <span className="gdm-conf" style={{ color: confColor(vp.confidence) }}>
                      confidence {pct(vp.confidence)}
                    </span>
                    {vp.veto_active && <Badge text="rule veto" color="#dc2626" />}
                  </div>
                  <div className="gdm-airow gdm-dim2">
                    {vp.n_rule_hits ?? 0} rule hit(s) · Δ drug–cosmetic {vp.delta_drug_cosmetic ?? '—'}
                    {vp.ml_enabled != null && ` · ML ${vp.ml_enabled ? 'on' : 'off'}`}
                  </div>
                  {Array.isArray(vp.citations) && vp.citations.length > 0 && (
                    <ul className="gdm-cites">{vp.citations.map((c, i) => <li key={i}>{c}</li>)}</ul>
                  )}
                </>
              ) : <div className="gdm-dim">Not yet scored — use “Re-run AI + VeriPass”.</div>}
            </div>

            {cand.status !== 'signed' && (
              <>
                <label>Rationale (why)
                  <textarea value={draft.gold_rationale || ''} onChange={(e) => setDraft({ ...draft, gold_rationale: e.target.value })} /></label>
                <label>Controlling citation
                  <input value={draft.controlling_citation || ''} onChange={(e) => setDraft({ ...draft, controlling_citation: e.target.value })} /></label>
                <label>Regulatory citations (comma-sep)
                  <input value={csv(draft.regulatory_citations)} onChange={(e) => setDraft({ ...draft, regulatory_citations: parseList(e.target.value) })} /></label>
                <label>Sources — document id
                  <input value={draft.source_document_id || ''} onChange={(e) => setDraft({ ...draft, source_document_id: e.target.value })} /></label>
                <label>Review flags (comma-sep)
                  <input value={csv(draft.review_flags)} onChange={(e) => setDraft({ ...draft, review_flags: parseList(e.target.value) })} /></label>
                <label>Notes
                  <textarea value={draft.notes || ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></label>
                <label className="gdm-check"><input type="checkbox" checked={!!draft.requires_dual_signoff}
                  onChange={(e) => setDraft({ ...draft, requires_dual_signoff: e.target.checked })} /> Requires dual sign-off (class-boundary)</label>
                <button className="gdm-btn" onClick={saveEdit}>Save edits</button>

                <div className="gdm-signrow">
                  <select value={signLabel} onChange={(e) => setSignLabel(e.target.value)}>
                    <option value="">— verified class —</option>
                    {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <input placeholder="sign-off comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
                  <button className="gdm-btn gdm-btn-accent" disabled={!signLabel}
                    onClick={() => act('sign', { label: signLabel, comment })}>Sign</button>
                  <button className="gdm-btn gdm-btn-danger" onClick={() => act('reject', { reason: comment })}>Reject</button>
                </div>
              </>
            )}
            {cand.status === 'signed' && (
              <div className="gdm-signed">
                Signed <b>{cand.expected_label}</b> ({cand.agreement}) by {cand.adjudicator} on {cand.adjudication_date}.
                {isSiteAdmin && <button className="gdm-btn gdm-btn-danger" onClick={() => act('reopen')}>Reopen (root)</button>}
              </div>
            )}

            {sel.signoffs?.length > 0 && (
              <div className="gdm-section"><h4>Sign-offs</h4>
                {sel.signoffs.map((s, i) => <div key={i} className="gdm-rev">{s.signer_name} → <b>{s.proposed_label}</b> {s.comment && `· ${s.comment}`}</div>)}</div>
            )}
            <div className="gdm-section"><h4>History</h4>
              {(sel.revisions || []).map((r, i) => (
                <div key={i} className="gdm-rev">
                  <span className="gdm-rev-act">{r.action}</span> · {r.changed_by_name} · {new Date(r.changed_at).toLocaleString()}
                  {r.comment && <div className="gdm-rev-c">{r.comment}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
